import './projects.css';
import { useEffect, useMemo, useRef, useState, type FormEvent, type PointerEvent } from 'react';
import { Avatar } from '@openai/apps-sdk-ui/components/Avatar';
import { Badge } from '@openai/apps-sdk-ui/components/Badge';
import { Button } from '@openai/apps-sdk-ui/components/Button';
import { Input } from '@openai/apps-sdk-ui/components/Input';
import { Textarea } from '@openai/apps-sdk-ui/components/Textarea';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Folder,
  Grid,
  Plus,
  Search,
} from '@openai/apps-sdk-ui/components/Icon';
import type { NewProject, Project, Task, TaskPatch, User } from '../shared/types';
import { errorMessage } from './api';
import { RepeatBadge } from './Recurrence';
import { Empty, ErrorNotice, Field, assigneeTone, formatDate } from './ui';

type TaskStatus = Task['status'];
const COLUMNS: { status: TaskStatus; label: string; empty: string }[] = [
  { status: 'todo', label: 'To do', empty: 'No tasks to do.' },
  { status: 'in_progress', label: 'In progress', empty: 'Move a task here when you start.' },
  { status: 'done', label: 'Done', empty: 'Finished work belongs here.' },
];
const ordered = <T extends { createdAt: string; id: string }>(a: T, b: T) =>
  a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);

export function ProjectSetup({
  onCreate,
  onCancel,
}: {
  onCreate: (payload: NewProject) => Promise<void>;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    const fields = new FormData(event.currentTarget);
    const name = String(fields.get('name') || '').trim();
    const description = String(fields.get('description') || '').trim();
    if (!name) {
      setError('Give your project a name.');
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      await onCreate({ name, description });
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }
  return (
    <div className="project-setup-page page-enter">
      <Button
        color="secondary"
        variant="ghost"
        size="sm"
        className="project-back"
        onClick={onCancel}
        disabled={busy}
      >
        <ArrowLeft />
        Projects
      </Button>
      <div className="project-setup-content">
        <div className="project-setup-icon">
          <Folder />
        </div>
        <span className="eyebrow">PROJECT SETUP</span>
        <h1>Create a project</h1>
        <p className="project-setup-description">
          Name your project and add a description. You can add tasks on the board.
        </p>
        <form className="stack-form" onSubmit={submit} aria-busy={busy}>
          <Field label="Project name">
            <Input
              name="name"
              placeholder="e.g. Website launch"
              required
              autoFocus
              maxLength={120}
              readOnly={busy}
              size="xl"
            />
          </Field>
          <Field
            label="Description (optional)"
            hint="A little context for everyone working on this project."
          >
            <Textarea
              name="description"
              placeholder="What are we working toward?"
              maxLength={2000}
              readOnly={busy}
              rows={4}
            />
          </Field>
          {error && <ErrorNotice message={error} />}
          <div className="project-setup-actions">
            <Button color="secondary" variant="outline" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button color="primary" type="submit" size="lg" loading={busy} disabled={busy}>
              Create project
              <ArrowRight />
            </Button>
          </div>
        </form>
        <div className="project-setup-note">
          <Grid />
          <span>Your project starts with a board: To do, In progress, and Done.</span>
        </div>
      </div>
    </div>
  );
}

export function Projects({
  projects,
  tasks,
  onOpen,
  onNew,
}: {
  projects: Project[];
  tasks: Task[];
  onOpen: (id: string) => void;
  onNew: () => void;
}) {
  const [query, setQuery] = useState('');
  const visible = useMemo(
    () =>
      [...projects]
        .sort(ordered)
        .filter((project) =>
          `${project.name} ${project.description}`.toLowerCase().includes(query.toLowerCase()),
        ),
    [projects, query],
  );
  return (
    <div className="projects-page page-enter">
      <div className="page-heading">
        <div>
          <span className="eyebrow">PROJECTS</span>
          <h1>
            Projects<span className="heading-count">{projects.length}</span>
          </h1>
          <p>Shared projects and their task progress.</p>
        </div>
        <Button color="primary" size="lg" onClick={onNew}>
          <Plus />
          New project
        </Button>
      </div>
      {projects.length > 0 && (
        <div className="projects-toolbar">
          <span>
            {projects.length} project{projects.length === 1 ? '' : 's'} in your workspace
          </span>
          <Input
            className="projects-search"
            aria-label="Search projects"
            startAdornment={<Search />}
            placeholder="Find a project…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      )}
      {projects.length === 0 ? (
        <Empty
          icon={<Folder />}
          title="No projects yet"
          description="Create a project to organize tasks and see progress on a shared board."
        >
          <Button color="primary" onClick={onNew}>
            <Plus />
            Create your first project
          </Button>
        </Empty>
      ) : visible.length === 0 ? (
        <Empty
          icon={<Search />}
          title="No projects found"
          description="Try a different name or clear your search."
        >
          <Button color="secondary" variant="outline" onClick={() => setQuery('')}>
            Clear search
          </Button>
        </Empty>
      ) : (
        <div className="projects-grid">
          {visible.map((project) => {
            const projectTasks = tasks.filter((task) => task.projectId === project.id);
            const done = projectTasks.filter((task) => task.status === 'done').length;
            const progress = projectTasks.length
              ? Math.round((done / projectTasks.length) * 100)
              : 0;
            return (
              <button
                key={project.id}
                className="project-tile"
                onClick={() => onOpen(project.id)}
                aria-label={`Open ${project.name}, ${done} of ${projectTasks.length} tasks completed`}
              >
                <div className="project-tile-top">
                  <span className="project-tile-icon">
                    <Folder />
                  </span>
                  <ArrowRight />
                </div>
                <h2>{project.name}</h2>
                <p>{project.description || 'No description'}</p>
                <div className="project-tile-progress">
                  <span className="project-progress-track" aria-hidden="true">
                    <span style={{ width: `${progress}%` }} />
                  </span>
                  <div>
                    <span>
                      {projectTasks.length} task{projectTasks.length === 1 ? '' : 's'}
                    </span>
                    <span>{done} done</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

type DragState = { task: Task; pointerId: number; startX: number; startY: number; active: boolean };
export function ProjectBoard({
  project,
  tasks,
  users,
  onNewTask,
  onEditTask,
  updateTask,
  onBack,
  onDelete,
  privateContext,
}: {
  project: Project;
  tasks: Task[];
  users: User[];
  onNewTask: (status?: TaskStatus) => void;
  onEditTask: (task: Task) => void;
  updateTask: (id: string, patch: TaskPatch) => Promise<void>;
  onBack: () => void;
  onDelete: () => void;
  privateContext?: import('react').ReactNode;
}) {
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const [pending, setPending] = useState<Set<string>>(new Set());
  const pendingRef = useRef(new Set<string>());
  const mounted = useRef(true);
  const currentProject = useRef(project.id);
  currentProject.current = project.id;
  const latestTasks = useRef(tasks);
  latestTasks.current = tasks;
  const [dragging, setDragging] = useState<{ task: Task; x: number; y: number } | null>(null);
  const [over, setOver] = useState<TaskStatus | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const draggedUntil = useRef(0);
  const boardRef = useRef<HTMLDivElement>(null);
  const projectTasks = useMemo(
    () => tasks.filter((task) => task.projectId === project.id).sort(ordered),
    [tasks, project.id],
  );
  const visible = useMemo(
    () =>
      projectTasks.filter((task) =>
        `${task.title} ${task.description}`.toLowerCase().includes(query.toLowerCase()),
      ),
    [projectTasks, query],
  );
  const completed = projectTasks.filter((task) => task.status === 'done').length;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      dragRef.current = null;
    };
  }, []);
  useEffect(() => {
    setQuery('');
    setError('');
    setAnnouncement('');
    dragRef.current = null;
    setDragging(null);
    setOver(null);
  }, [project.id]);
  useEffect(() => {
    if (dragRef.current && !projectTasks.some((task) => task.id === dragRef.current!.task.id)) {
      dragRef.current = null;
      setDragging(null);
      setOver(null);
    }
  }, [projectTasks]);
  async function move(task: Task, status: TaskStatus) {
    const latestTask = latestTasks.current.find((current) => current.id === task.id);
    if (
      !latestTask ||
      latestTask.projectId !== project.id ||
      currentProject.current !== project.id
    ) {
      setError('This task is no longer in this project. Refresh the board before moving it.');
      return;
    }
    if (
      latestTask.status === status ||
      pendingRef.current.has(task.id) ||
      (latestTask.status === 'done' && latestTask.recurrence)
    )
      return;
    const projectId = project.id;
    const focusedControl = document.activeElement;
    const restoreFocus = focusedControl?.id === `move-${task.id}`;
    pendingRef.current.add(task.id);
    setPending(new Set(pendingRef.current));
    setError('');
    try {
      await updateTask(task.id, { status });
      if (mounted.current && currentProject.current === projectId) {
        setAnnouncement(
          `Moved ${latestTask.title} to ${COLUMNS.find((column) => column.status === status)!.label}.`,
        );
        if (restoreFocus)
          requestAnimationFrame(() => {
            if (
              mounted.current &&
              currentProject.current === projectId &&
              (document.activeElement === document.body ||
                document.activeElement === focusedControl)
            )
              document.getElementById(`move-${task.id}`)?.focus({ preventScroll: true });
          });
      }
    } catch (error) {
      if (mounted.current && currentProject.current === projectId)
        setError(`Could not move “${latestTask.title}”. ${errorMessage(error)}`);
    } finally {
      pendingRef.current.delete(task.id);
      if (mounted.current) setPending(new Set(pendingRef.current));
    }
  }
  function targetColumn(x: number, y: number): TaskStatus | null {
    const column = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-kanban-status]');
    if (!column || !boardRef.current?.contains(column)) return null;
    return COLUMNS.some((item) => item.status === column.dataset.kanbanStatus)
      ? (column.dataset.kanbanStatus as TaskStatus)
      : null;
  }
  function startDrag(event: PointerEvent<HTMLButtonElement>, task: Task) {
    if (
      pendingRef.current.has(task.id) ||
      event.button !== 0 ||
      (task.status === 'done' && task.recurrence)
    )
      return;
    dragRef.current = {
      task,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      active: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }
  function continueDrag(event: PointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (!drag.active && Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 6)
      return;
    drag.active = true;
    event.preventDefault();
    setDragging({ task: drag.task, x: event.clientX, y: event.clientY });
    setOver(targetColumn(event.clientX, event.clientY));
    const viewport = boardRef.current;
    if (viewport) {
      const rect = viewport.getBoundingClientRect();
      if (event.clientX > rect.right - 45) viewport.scrollLeft += 15;
      else if (event.clientX < rect.left + 45) viewport.scrollLeft -= 15;
    }
  }
  function endDrag(event: PointerEvent<HTMLButtonElement>, cancelled = false) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    const destination =
      !cancelled && drag.active ? targetColumn(event.clientX, event.clientY) : null;
    dragRef.current = null;
    setDragging(null);
    setOver(null);
    if (drag.active) {
      draggedUntil.current = Date.now() + 300;
      event.preventDefault();
      if (destination) void move(drag.task, destination);
    }
  }
  const today = new Date();
  const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return (
    <div className="project-board-page page-enter">
      <Button color="secondary" variant="ghost" size="sm" className="project-back" onClick={onBack}>
        <ArrowLeft />
        Projects
      </Button>
      <div className="page-heading project-board-heading">
        <div>
          <h1>
            <span className="project-heading-icon">
              <Folder />
            </span>
            {project.name}
          </h1>
          <p>{project.description || 'Project task board.'}</p>
        </div>
        <div className="project-board-actions">
          <Button color="secondary" variant="ghost" size="lg" onClick={onDelete}>
            Delete project
          </Button>
          <Button color="primary" size="lg" onClick={() => onNewTask('todo')}>
            <Plus />
            New task
          </Button>
        </div>
      </div>
      {privateContext}
      <div className="project-board-toolbar">
        <div className="board-view-label">
          <Grid />
          <span>Board</span>
          <span className="board-task-summary">
            {projectTasks.length} task{projectTasks.length === 1 ? '' : 's'}
            <span>·</span>
            {completed} done
          </span>
        </div>
        <Input
          aria-label="Search project tasks"
          startAdornment={<Search />}
          placeholder="Search this project…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="board-search"
        />
      </div>
      {error && <ErrorNotice message={error} />}
      <span className="sr-only" role="status" aria-live="polite">
        {announcement}
      </span>
      <div className="kanban-scroll" ref={boardRef} aria-label={`${project.name} task board`}>
        <div className="kanban-columns">
          {COLUMNS.map((column) => {
            const columnTasks = visible.filter((task) => task.status === column.status);
            const isTarget =
              !!dragging && over === column.status && dragging.task.status !== column.status;
            return (
              <section
                key={column.status}
                className={`kanban-column ${isTarget ? 'drop-target' : ''}`}
                data-kanban-status={column.status}
                aria-label={`${column.label}, ${columnTasks.length} tasks`}
              >
                <header className="kanban-column-header">
                  <span className={`status-dot ${column.status}`} />
                  <h2>{column.label}</h2>
                  <span className="kanban-column-count">{columnTasks.length}</span>
                  <Button
                    color="secondary"
                    variant="ghost"
                    size="xs"
                    uniform
                    aria-label={`Add task to ${column.label}`}
                    onClick={() => onNewTask(column.status)}
                  >
                    <Plus />
                  </Button>
                </header>
                <div className="kanban-task-list">
                  {columnTasks.map((task) => {
                    const assignee = users.find((user) => user.id === task.assigneeId);
                    const tone = assigneeTone(users, task.assigneeId);
                    const overdue =
                      !!task.dueDate && task.dueDate < localDate && task.status !== 'done';
                    const isPending = pending.has(task.id);
                    return (
                      <article
                        className={`kanban-task ${dragging?.task.id === task.id ? 'drag-source' : ''} ${isPending ? 'move-pending' : ''}`}
                        key={task.id}
                        data-assignee-tone={tone}
                        aria-busy={isPending}
                      >
                        <div className="kanban-task-top">
                          <code>{task.id.slice(0, 6)}</code>
                          <div>
                            {task.priority !== 'normal' && (
                              <Badge
                                color={task.priority === 'high' ? 'warning' : 'secondary'}
                                variant="soft"
                              >
                                {task.priority === 'high' ? 'High priority' : 'Low priority'}
                              </Badge>
                            )}
                            <button
                              className="task-drag-handle"
                              type="button"
                              aria-label={`Move ${task.title}; activate to choose a status or drag to a column`}
                              title="Drag to a column, or click to choose status"
                              disabled={isPending || (task.status === 'done' && !!task.recurrence)}
                              onPointerDown={(event) => startDrag(event, task)}
                              onPointerMove={continueDrag}
                              onPointerUp={(event) => endDrag(event)}
                              onPointerCancel={(event) => endDrag(event, true)}
                              onClick={(event) => {
                                if (Date.now() < draggedUntil.current) return;
                                event.currentTarget
                                  .closest('article')
                                  ?.querySelector('select')
                                  ?.focus();
                              }}
                            >
                              <svg viewBox="0 0 12 16" width="12" height="16" aria-hidden="true">
                                <circle cx="4" cy="4" r="1" />
                                <circle cx="8" cy="4" r="1" />
                                <circle cx="4" cy="8" r="1" />
                                <circle cx="8" cy="8" r="1" />
                                <circle cx="4" cy="12" r="1" />
                                <circle cx="8" cy="12" r="1" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <button
                          className={`kanban-task-content ${task.status === 'done' ? 'task-done' : ''}`}
                          onClick={() => onEditTask(task)}
                          disabled={isPending}
                        >
                          <h3>{task.title}</h3>
                          {task.description && <p>{task.description}</p>}
                        </button>
                        {task.recurrence && (
                          <div className="kanban-task-repeat">
                            <RepeatBadge recurrence={task.recurrence} />
                          </div>
                        )}
                        <div className="kanban-task-meta">
                          {task.dueDate && (
                            <span className={`kanban-task-date ${overdue ? 'overdue' : ''}`}>
                              {overdue && 'Overdue · '}
                              {formatDate(task.dueDate)}
                            </span>
                          )}
                          {assignee ? (
                            <span
                              className="kanban-assignee"
                              title={`Assigned to ${assignee.name}`}
                            >
                              <Avatar name={assignee.name} size={22} color={tone} />
                              <span>{assignee.name.split(' ')[0]}</span>
                            </span>
                          ) : (
                            <span className="kanban-unassigned">Unassigned</span>
                          )}
                        </div>
                        <div className="kanban-task-status">
                          <label className="sr-only" htmlFor={`move-${task.id}`}>
                            Status for {task.title}
                          </label>
                          <select
                            id={`move-${task.id}`}
                            aria-label={`Status for ${task.title}`}
                            value={task.status}
                            disabled={isPending || (task.status === 'done' && !!task.recurrence)}
                            onChange={(event) => void move(task, event.target.value as TaskStatus)}
                          >
                            {COLUMNS.map((option) => (
                              <option value={option.status} key={option.status}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {isPending ? (
                            <span role="status">Saving…</span>
                          ) : task.status === 'done' ? (
                            <CheckCircle />
                          ) : (
                            <span className="kanban-move-hint">Move to change status</span>
                          )}
                        </div>
                      </article>
                    );
                  })}
                  {columnTasks.length === 0 && (
                    <div className="kanban-column-empty">
                      <span className={`status-dot ${column.status}`} />
                      <p>{query ? 'No matching tasks.' : column.empty}</p>
                      {!query && (
                        <button onClick={() => onNewTask(column.status)}>
                          <Plus />
                          Add a task
                        </button>
                      )}
                    </div>
                  )}
                  {columnTasks.length > 0 && (
                    <button className="kanban-add-task" onClick={() => onNewTask(column.status)}>
                      <Plus />
                      Add task
                    </button>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
      <footer className="board-footer">
        <span>
          {query
            ? `${visible.length} matching task${visible.length === 1 ? '' : 's'}`
            : 'Drag a task by its handle to change status.'}
        </span>
        <span>Or use the status menu on any task.</span>
      </footer>
      {dragging && (
        <div
          className="kanban-drag-ghost"
          style={{ left: dragging.x + 14, top: dragging.y + 14 }}
          aria-hidden="true"
        >
          <Folder />
          <span>{dragging.task.title}</span>
        </div>
      )}
    </div>
  );
}
